import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";

import axios from "@atd/app/axiosClient";
import { uploadFileResumable } from "@atd/utils/resumableUpload";

const useAttachment = (files, params) => {
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (files) {
      setAttachments(files || []);
    } else {
      setAttachments([]);
    }
  }, [files]);

  const onSetAttachments = useCallback(
    async (currentFiles) => {
      if (!currentFiles) {
        return;
      }
      const file = currentFiles[0];

      setProgress(0);
      setIsUploading(true);

      try {
        // 1. Ask the API for a resumable GCS session (JSON only, no file bytes).
        const { data } = await axios({
          url: "/attach/resumable",
          method: "POST",
          data: {
            filename: file.name,
            size: file.size,
            content_type: file.type,
            ...params,
          },
        });

        if (data?.status === "error") {
          throw Error(data.message);
        }

        if (data.code !== 201 || !data.results?.upload_url) {
          throw Error(data?.message || "Could not start upload");
        }

        // upload_url is only needed for the upload; keep the rest as the
        // attachment object the UI already consumes.
        const { upload_url: uploadUrl, ...attachment } = data.results;
        delete attachment.object_name;
        delete attachment.content_type;

        // 2. Upload the file straight to GCS in chunks.
        await uploadFileResumable(uploadUrl, file, setProgress);

        setAttachments([attachment, ...attachments]);
        setTimeout(() => setProgress(0), 1000);
        return { ...data, results: attachment };
      } catch (error) {
        toast.error(error.message);
        setProgress(0);
      } finally {
        setIsUploading(false);
      }

      return null;
    },
    [attachments, params],
  );

  const onRemoveAttachment = useCallback(
    (index) => {
      const updatedAttachments = [...attachments];
      if (index > -1) {
        updatedAttachments.splice(index, 1); // 2nd parameter means remove one item only
      }
      // const updatedAttachments = [
      //   ...attachments?.slice(0, index),
      //   ...attachments?.splice(index + 1),
      // ];
      setAttachments(updatedAttachments);
    },
    [attachments],
  );

  return {
    progress,
    attachments,
    isUploading,
    setAttachments,
    onRemoveAttachment,
    onSetAttachments,
  };
};

export default useAttachment;
